// pages/FavoriteProductDetailPage.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { getProductView } from '../api/endpoints';
import type { ProductView, ProductViewIngredient } from '../types/api';
import { API_BASE_URL } from '../api/config';
import { useUser } from '../contexts/UserContext';
import useFavoriteProduct from '../hooks/useFavoriteProduct';
import { postProductView } from '../api/telemetry';
import { ProductSearchModal } from '../components/ProductSearchModal';
import ConfirmationDialog from '../components/ui/ConfirmationDialog';
import { getErrorMessage } from '../utils/errors';
import { shareOrCopyUrl } from '../utils/scannerIngredient';

// ── Icons ────────────────────────────────────────────────────
const SZ = { width: 18, height: 18 } as const;

const IconBack = () => (
  <svg viewBox="0 0 24 24" {...SZ} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 12H5" /><path d="m12 19-7-7 7-7" />
  </svg>
);
const IconSpark = () => (
  <svg viewBox="0 0 24 24" {...SZ} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2v4" /><path d="M12 18v4" /><path d="M4.93 4.93 7.76 7.76" /><path d="M16.24 16.24l2.83 2.83" />
    <path d="M2 12h4" /><path d="M18 12h4" /><path d="M4.93 19.07 7.76 16.24" /><path d="M16.24 7.76l2.83-2.83" />
  </svg>
);
const IconLink = () => (
  <svg viewBox="0 0 24 24" {...SZ} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l2-2a5 5 0 1 0-7.07-7.07l-1.2 1.2" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-2 2a5 5 0 1 0 7.07 7.07l1.2-1.2" />
  </svg>
);
const IconScale = () => (
  <svg viewBox="0 0 24 24" {...SZ} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3v18" /><path d="M5 7h14" />
    <path d="m7 7-4 7a4 4 0 0 0 4 4 4 4 0 0 0 4-4l-4-7Zm10 0-4 7a4 4 0 0 0 4 4 4 4 0 0 0 4-4l-4-7Z" />
  </svg>
);
const IconHeart = ({ filled = false }: { filled?: boolean }) => (
  <svg viewBox="0 0 24 24" {...SZ} fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 21s-6.4-4.35-9.2-8.06C1.02 11.66 1 8.65 3.2 6.4 5.4 4.2 8.7 4.1 10.9 6.3L12 7.41l1.1-1.1c2.2-2.2 5.5-2.1 7.7.1 2.2 2.24 2.18 5.26.2 6.54C18.4 16.65 12 21 12 21Z" />
  </svg>
);
const IconShare = () => (
  <svg viewBox="0 0 24 24" {...SZ} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7" />
    <path d="M16 6l-4-4-4 4" /><path d="M12 2v14" />
  </svg>
);
const IconCopy = () => (
  <svg viewBox="0 0 24 24" {...SZ} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);
const IconRetry = () => (
  <svg viewBox="0 0 24 24" {...SZ} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.13-3.36L23 10" /><path d="M20.49 15a9 9 0 0 1-14.13 3.36L1 14" />
  </svg>
);
const IconShopping = () => (
  <svg viewBox="0 0 24 24" {...SZ} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
    <path d="M3 6h18" /><path d="M16 10a4 4 0 0 1-8 0" />
  </svg>
);

// ── Utils ────────────────────────────────────────────────────
const APIImg = (url?: string | null) => {
  if (!url) return null;
  if (/^https?:/i.test(url)) return url;
  const base = API_BASE_URL.endsWith('/') ? API_BASE_URL : API_BASE_URL + '/';
  try { return new URL(url, base).toString(); } catch { return null; }
};

const copyText = async (text?: string | null) => {
  if (!text) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      // Legacy fallback when Clipboard API is unavailable or denied.
      // eslint-disable-next-line -- pre-Clipboard-API fallback; no-deprecated exists only in the strict flat config
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      return ok;
    } catch {
      return false;
    }
  }
};

function ingredientLabel(ing: ProductViewIngredient): string {
  return ing.name ?? ing.original_text;
}

// ── Page ────────────────────────────────────────────────────
export default function FavoriteProductDetailPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const numericId = id ? Number(id) : null;
  const location = useLocation();
  const state = location.state as { barcode?: string; source?: string } | null;

  const [product, setProduct] = useState<ProductView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [zoom, setZoom] = useState(false);
  const [searchingProduct, setSearchingProduct] = useState<{ keyword: string; title: string } | null>(null);
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);

  const { costFor, canAfford } = useUser();
  const scanCost = costFor('product_barcode');
  const outOfQuota = !canAfford('product_barcode');

  const favorite = useFavoriteProduct({ productId: numericId ?? undefined, disabled: !numericId, initial: product?.favorited ?? undefined });
  const fav = favorite.favorited;
  const ariaPressed: boolean | 'mixed' | undefined = fav ?? undefined;

  const makeAnalysisAction = useCallback((): (() => void) | null => {
    if (!product?.product_id) return null;
    const productId = product.product_id;
    const barcode = product.barcode ?? '';
    const source = state?.source ?? 'db';
    return () => { nav(`/scanner/result/${productId}`, { state: { barcode, source } }); };
  }, [nav, product?.product_id, product?.barcode, state?.source]);

  const makeCompatibilityAction = useCallback((): (() => void) | null => {
    if (!product?.product_id) return null;
    const productId = product.product_id;
    const barcode = product.barcode ?? '';
    return () => { nav('/compatibility', { state: { prefill: { barcodeA: barcode, productIdA: productId } } }); };
  }, [nav, product?.product_id, product?.barcode]);

  const makeComparisonAction = useCallback((): (() => void) | null => {
    if (!product?.product_id) return null;
    const productId = product.product_id;
    const barcode = product.barcode ?? '';
    return () => { nav('/comparison', { state: { prefill: { barcodeA: barcode, productIdA: productId } } }); };
  }, [nav, product?.product_id, product?.barcode]);

  const load = useCallback(() => {
    if (!numericId) return;
    setLoading(true);
    void postProductView(numericId).catch(() => { /* telemetry non-fatal */ });
    getProductView(numericId)
      .then((info) => { setProduct(info); setError(null); })
      .catch((error_: unknown) => { setError(getErrorMessage(error_, 'Failed to load product')); })
      .finally(() => { setLoading(false); });
  }, [numericId]);

  useEffect(() => { load(); }, [load]);

  const [compact, setCompact] = useState(false);
  const heroRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;
    const io = new IntersectionObserver(
      (entries) => { setCompact(!entries[0].isIntersecting); },
      { threshold: 0.01 }
    );
    io.observe(hero);
    return () => { io.disconnect(); };
  }, []);

  const image = APIImg(product?.image_url);
  const ingredients = useMemo(() => product?.ingredients ?? [], [product?.ingredients]);

  const notify = (msg: string) => {
    setCopied(msg);
    window.setTimeout(() => { setCopied(null); }, 1600);
  };

  const onShare = async () => {
    const title = product?.name ?? 'Product';
    const text = `${title}${product?.brand ? ` — ${product.brand}` : ''}`;
    try {
      await shareOrCopyUrl(text, window.location.href, () => { notify('Link copied'); });
    } catch { /* user cancelled */ }
  };

  const copyBarcode = async () => {
    const ok = await copyText(product?.barcode ?? undefined);
    notify(ok ? 'Barcode copied' : 'Could not copy');
  };

  const copyIngredients = async () => {
    const text = ingredients.map(ingredientLabel).filter(Boolean).join(', ');
    const ok = await copyText(text);
    notify(ok ? 'Ingredients copied' : 'Could not copy');
  };

  const sendToAnalysis = () => {
    const action = makeAnalysisAction();
    if (action) setConfirmAction(() => action);
  };
  const sendToCompatibility = () => {
    if (outOfQuota) { notify('Insufficient credits — top up or wait for your monthly reset'); return; }
    const action = makeCompatibilityAction();
    if (action) setConfirmAction(() => action);
  };
  const sendToComparison = () => {
    if (outOfQuota) { notify('Insufficient credits — top up or wait for your monthly reset'); return; }
    const action = makeComparisonAction();
    if (action) setConfirmAction(() => action);
  };

  const [q, setQ] = useState('');
  const filteredIngs = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return ingredients;
    return ingredients.filter((ing) =>
      ingredientLabel(ing).toLowerCase().includes(s));
  }, [ingredients, q]);

  return (
    <div style={{ maxWidth: 860, marginInline: 'auto', paddingBottom: 'var(--s-10)' }}>

      {/* Sticky back-nav header */}
      <header className={`page-header${compact ? ' page-header--compact' : ''}`}>
        <button className="btn btn--icon" onClick={() => { nav(-1); }} aria-label="Back">
          <IconBack />
        </button>
        <div className={`page-header__title${compact ? ' page-header__title--compact' : ''}`}>
          {compact ? (product?.name ?? 'Product details') : 'Product details'}
        </div>
        <button className="btn btn--icon" onClick={onShare} aria-label="Share">
          <IconShare />
        </button>
        <button
          className={`btn btn--icon${fav ? ' btn--icon--active' : ''}`}
          onClick={() => { void favorite.toggle(); }}
          disabled={favorite.loading || !numericId}
          aria-pressed={ariaPressed}
          aria-label={fav ? 'Remove from favorites' : 'Add to favorites'}
        >
          <IconHeart filled={!!fav} />
        </button>
      </header>

      <main>
        {/* Hero card */}
        <div
          ref={heroRef}
          className="card card--bordered card--xl stack-4"
          style={{ marginTop: 'var(--s-4)' }}
        >
          {/* Title block */}
          <div className="stack-2">
            <h1 className="t-h3" style={{ margin: 0 }}>
              {product?.name ?? 'Product details'}
            </h1>
            <p className="t-small t-mute" style={{ margin: 0 }}>
              {product?.brand ?? 'Brand unavailable'}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-2)', marginTop: 'var(--s-1)' }}>
              <span className="pill">ID {product?.product_id ?? '—'}</span>
              <button
                type="button"
                className="pill"
                onClick={copyBarcode}
                title="Copy barcode"
                style={{
                  cursor: product?.barcode ? 'pointer' : 'default',
                  border: 'none',
                  font: '500 12px/1 var(--f-text)',
                }}
              >
                Barcode {product?.barcode ?? '—'}
              </button>
            </div>
          </div>

          {/* Image + actions grid */}
          <div className="detail-grid">
            {/* Product image */}
            <button
              type="button"
              onClick={() => { if (image) setZoom(true); }}
              className="frame"
              aria-label="View full image"
              style={{
                height: 240,
                display: 'grid',
                placeItems: 'center',
                background: 'var(--c-sand)',
                position: 'relative',
                cursor: image ? 'zoom-in' : 'default',
                border: 'none',
                width: '100%',
              }}
            >
              {image ? (
                <img
                  loading="lazy"
                  src={image}
                  alt={product?.name ?? 'Product image'}
                  style={{
                    maxHeight: 220,
                    maxWidth: 180,
                    objectFit: 'contain',
                    transition: 'transform var(--t-base) var(--ease)',
                  }}
                />
              ) : (
                <span className="t-small t-mute">No image</span>
              )}
              {image && (
                <span
                  className="pill pill--dark"
                  style={{
                    position: 'absolute',
                    bottom: 'var(--s-2)',
                    right: 'var(--s-2)',
                    fontSize: 'var(--fs-label)',
                    pointerEvents: 'none',
                  }}
                >
                  Tap to zoom
                </span>
              )}
            </button>

            {/* Actions + ingredients */}
            <div className="stack-4">
              {/* Action buttons */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-2)' }}>
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  onClick={sendToAnalysis}
                  disabled={!product?.product_id}
                >
                  <IconSpark />Analyze
                </button>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={sendToCompatibility}
                  disabled={!product?.product_id || outOfQuota}
                >
                  <IconLink />Compatibility
                </button>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={sendToComparison}
                  disabled={!product?.product_id || outOfQuota}
                >
                  <IconScale />Compare
                </button>
                <button
                  type="button"
                  className="btn btn--coral btn--sm"
                  onClick={() => { setSearchingProduct({
                    keyword: `${product?.brand ?? ''} ${product?.name ?? ''}`.trim(),
                    title: product?.name ?? 'Product',
                  }); }}
                >
                  <IconShopping />Shop
                </button>
              </div>

              {outOfQuota && (
                <p className="notice notice--warn" style={{ margin: 0 }}>
                  Insufficient credits{scanCost != null ? ` (this costs ${scanCost})` : ''} — <a href="/wallet" className="t-teal">buy credits</a> or wait for your monthly reset.
                </p>
              )}

              {/* Ingredients */}
              <section
                className="card card--sand card--bordered stack-3"
                style={{ padding: 'var(--s-4)' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span className="t-label">Ingredients</span>
                  <button type="button" className="btn btn--ghost btn--sm" onClick={copyIngredients}>
                    <IconCopy />Copy
                  </button>
                </div>

                <div className="stack-3">
                  <div style={{ position: 'relative' }}>
                    <input
                      className="field"
                      value={q}
                      onChange={(e) => { setQ(e.target.value); }}
                      placeholder="Search ingredients…"
                    />
                    {q && (
                      <button
                        onClick={() => { setQ(''); }}
                        style={{
                          position: 'absolute',
                          right: 'var(--s-3)',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          font: '500 var(--fs-small)/1 var(--f-text)',
                          color: 'var(--ink-muted)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                        }}
                      >
                        Clear
                      </button>
                    )}
                  </div>

                  <ul style={{
                    listStyle: 'none',
                    margin: 0,
                    padding: 0,
                    maxHeight: 400,
                    overflowY: 'auto',
                  }}>
                    {filteredIngs.length > 0 ? (
                      filteredIngs.map((ing, idx) => (
                        <li
                          key={ing.id ?? `pos-${ing.position ?? idx}`}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--s-3)',
                            padding: 'var(--s-2) var(--s-1)',
                          }}
                        >
                          <span style={{
                            minWidth: 22,
                            flexShrink: 0,
                            textAlign: 'right',
                            font: '500 var(--fs-label)/1 var(--f-text)',
                            color: 'var(--ink-muted)',
                            letterSpacing: '0.04em',
                          }}>
                            {ing.position ?? '·'}
                          </span>
                          {ing.id ? (
                            <Link
                              to={`/ingredients/${ing.id}`}
                              className="link--subtle t-small"
                              style={{ flex: 1, lineHeight: 1.45 }}
                            >
                              {ingredientLabel(ing)}
                            </Link>
                          ) : (
                            <span className="t-small" style={{ flex: 1, lineHeight: 1.45 }}>
                              {ingredientLabel(ing)}
                            </span>
                          )}
                          {ing.verified && (
                            <span
                              title="Verified COSING ingredient"
                              aria-label="Verified COSING ingredient"
                              style={{ flexShrink: 0, color: 'var(--ink-muted)', font: '500 var(--fs-label)/1 var(--f-text)' }}
                            >
                              ✓
                            </span>
                          )}
                        </li>
                      ))
                    ) : (
                      <li className="t-small t-mute" style={{ padding: 'var(--s-3) var(--s-1)' }}>
                        {q ? 'No matches.' : 'No ingredients on record.'}
                      </li>
                    )}
                  </ul>
                </div>
              </section>
            </div>
          </div>

          {loading && (
            <p className="t-small t-mute" style={{ margin: 0 }}>Loading…</p>
          )}
          {error && (
            <div className="notice notice--error">
              <IconRetry />
              <span style={{ flex: 1 }}>{error}</span>
              <button onClick={load} className="btn btn--ghost btn--sm">Retry</button>
            </div>
          )}
        </div>
      </main>

      {/* Zoom lightbox — portalled to body to escape transform stacking context */}
      {zoom && image && createPortal(
        <div
          role="button"
          tabIndex={0}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            background: 'rgba(31,36,33,.92)',
            display: 'grid',
            placeItems: 'center',
          }}
          onClick={() => { setZoom(false); }}
          onKeyDown={(e) => {
            if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setZoom(false);
            }
          }}
        >
          <img
            src={image}
            alt={product?.name ?? 'Product'}
            style={{ maxHeight: '85vh', maxWidth: '90vw', objectFit: 'contain' }}
          />
        </div>,
        document.body
      )}

      {/* Toast */}
      {copied && (
        <div style={{
          position: 'fixed',
          bottom: 80,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 50,
        }}>
          <span className="pill pill--dark">{copied}</span>
        </div>
      )}

      {searchingProduct && (
        <ProductSearchModal
          keyword={searchingProduct.keyword}
          titleOverride={searchingProduct.title}
          onClose={() => { setSearchingProduct(null); }}
        />
      )}

      <ConfirmationDialog
        open={!!confirmAction}
        onClose={() => { setConfirmAction(null); }}
        onConfirm={() => {
          if (confirmAction) confirmAction();
          setConfirmAction(null);
        }}
        title="Confirm Scan"
        description="This will use credits. Are you sure you want to proceed?"
        confirmLabel="Proceed"
        cancelLabel="Cancel"
      />
    </div>
  );
}
